import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/ui/components/ui/dialog";
import { Button } from "@/ui/components/ui/button";

interface ProfilePhotoCropDialogProps {
  open: boolean;
  imageSrc: string;
  mimeType?: string;
  onClose: () => void;
  onSave: (blob: Blob) => Promise<void> | void;
}

const BOX_SIZE = 320;
const OUTPUT_SIZE = 512;

/**
 * Lightweight square crop dialog that lets the user pan/zoom the image before upload.
 */
export function ProfilePhotoCropDialog({
  open,
  imageSrc,
  mimeType = "image/jpeg",
  onClose,
  onSave,
}: ProfilePhotoCropDialogProps) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [baseScale, setBaseScale] = useState(1);
  const [zoom, setZoom] = useState(1.1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setImageLoaded(false);
      setZoom(1.1);
      setOffset({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [open]);

  const onImageLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    const fitScale = Math.max(BOX_SIZE / img.naturalWidth, BOX_SIZE / img.naturalHeight);
    setBaseScale(fitScale);
    setImageLoaded(true);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!imageLoaded) return;
    setIsDragging(true);
    dragRef.current = { x: e.clientX, y: e.clientY };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging || !dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    dragRef.current = { x: e.clientX, y: e.clientY };
    setOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    dragRef.current = null;
  };

  const scale = useMemo(() => baseScale * zoom, [baseScale, zoom]);

  const buildCroppedBlob = async (): Promise<Blob> => {
    const img = imgRef.current;
    if (!img) throw new Error("Image not loaded");

    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");

    // Mirror the transform used in the preview box
    const scaleFactor = OUTPUT_SIZE / BOX_SIZE;
    ctx.save();
    ctx.translate((OUTPUT_SIZE / 2) + offset.x * scaleFactor, (OUTPUT_SIZE / 2) + offset.y * scaleFactor);
    ctx.scale(scale * scaleFactor, scale * scaleFactor);
    ctx.translate(-img.naturalWidth / 2, -img.naturalHeight / 2);
    ctx.drawImage(img, 0, 0);
    ctx.restore();

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Failed to create blob"))),
        mimeType,
        0.92
      );
    });
    return blob;
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const blob = await buildCroppedBlob();
      await onSave(blob);
      onClose();
    } catch (err) {
      console.error("[ProfilePhotoCropDialog] save failed", err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Crop profile photo</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div
            className="relative bg-black overflow-hidden rounded-xl touch-none select-none"
            style={{ width: BOX_SIZE, height: BOX_SIZE, margin: "0 auto" }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop preview"
              className="absolute inset-0"
              style={{
                left: "50%",
                top: "50%",
                transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                transformOrigin: "center center",
                userSelect: "none",
                pointerEvents: "none",
              }}
              onLoad={onImageLoad}
            />
            <div className="absolute inset-0 pointer-events-none ring-2 ring-white/60 rounded-xl" />
          </div>

          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">Zoom</div>
            <input
              type="range"
              min={1}
              max={2.5}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value) || 1)}
              className="w-full"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!imageLoaded || isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
